import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { SITE_URL, SITE_NAME, OG_IMAGE, LANG_MAP } from "../lib/site.js";

// Sets/updates <head> tags imperatively to guarantee deduplication
// (React 19 hoisting does not remove baseline tags from index.html).
const upsert = (selector, create, updates) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(updates).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const setMeta = (attr, key, content) =>
  upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  }, { content });

const setLink = (rel, href, extra = {}) => {
  // hreflang links: 1 per hreflang value
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  return upsert(selector, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    return l;
  }, { href, ...extra });
};

export const Seo = ({ title, description, image, type = "website" }) => {
  const { pathname } = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const cleanPath = pathname === "/" ? "" : pathname.replace(/\/$/, "");
    const url = `${SITE_URL}${cleanPath}`;
    const ogImage = image || OG_IMAGE;
    const ogLocale = LANG_MAP[lang] || LANG_MAP.pt;

    document.title = title;

    setMeta("name", "description", description);
    setLink("canonical", url);

    // hreflang alternates — same URL for all languages (SPA i18n via localStorage)
    setLink("alternate", url, { hreflang: "pt" });
    setLink("alternate", url, { hreflang: "en" });
    setLink("alternate", url, { hreflang: "es" });
    setLink("alternate", url, { hreflang: "x-default" });

    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", ogLocale);
    setMeta("property", "og:url", url);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:alt", title);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Ensure <html lang> matches current language
    document.documentElement.lang = lang;
  }, [pathname, lang, title, description, image, type]);

  return null;
};
